import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { i18nResources } from "@/i18n/resources";

export const supportedLanguages = [
  {
    code: "en-US",
    labelKey: "language.english",
    shortLabel: "EN",
  },
  {
    code: "zh-CN",
    labelKey: "language.chinese",
    shortLabel: "中文",
  },
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number]["code"];

const LOCALE_STORAGE_KEY = "locale";
const LEGACY_LOCALE_STORAGE_KEY = "ours-lang";

export function normalizeLanguage(
  language: string | null | undefined,
): SupportedLanguage | null {
  if (!language) return null;
  const normalized = language.toLowerCase();
  if (normalized.startsWith("zh")) return "zh-CN";
  if (normalized.startsWith("en")) return "en-US";
  return null;
}

function readStoredLanguage() {
  try {
    return (
      normalizeLanguage(window.localStorage.getItem(LOCALE_STORAGE_KEY)) ??
      normalizeLanguage(window.localStorage.getItem(LEGACY_LOCALE_STORAGE_KEY))
    );
  } catch {
    return null;
  }
}

function getInitialLanguage(): SupportedLanguage {
  if (typeof window === "undefined") return "zh-CN";
  return (
    readStoredLanguage() ??
    normalizeLanguage(window.navigator.language) ??
    "zh-CN"
  );
}

const initialLanguage = getInitialLanguage();

void i18n.use(initReactI18next).init({
  resources: i18nResources,
  lng: initialLanguage,
  fallbackLng: "en-US",
  supportedLngs: supportedLanguages.map(({ code }) => code),
  load: "currentOnly",
  keySeparator: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function persistLanguage(language: string) {
  const normalizedLanguage = normalizeLanguage(language) ?? "en-US";
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLanguage);
    window.localStorage.setItem(
      LEGACY_LOCALE_STORAGE_KEY,
      normalizedLanguage === "zh-CN" ? "zh" : "en",
    );
  } catch {
    // Language still remains active for the current session.
  }

  document.documentElement.lang = normalizedLanguage;
  document.title = i18n.t("meta.title", { lng: normalizedLanguage });
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute(
      "content",
      i18n.t("meta.description", { lng: normalizedLanguage }),
    );
}

persistLanguage(initialLanguage);
i18n.on("languageChanged", persistLanguage);

export default i18n;
